import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from './supabase'
import {
  buildComplianceState,
  logClientEmailAction,
} from './compliance'

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const BM = {
  name: 'Benchmark Insurance Group Pty Ltd',
  trading: 'Benchmark Insurance Brokers',
  abn: '33 694 361 549',
  ar: '001320555',
  phone: '1300 553 519',
  email: 'info@benchmarkinsurance.com.au',
  website: 'https://benchmarkinsurance.com.au/',
  licensee: 'McLardy McShane Partners Pty Ltd',
  lic_abn: '14 064 465 309',
  lic_afsl: '232987',
}

const FSG_URL           = 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-FSG-Mar-26.pdf'
const TERMS_URL         = 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-Terms-of-Engagement-Mar-26.pdf'
const PRIVACY_URL       = 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-Privacy-Policy-Mar-26.pdf'
const CODE_URL          = 'https://niba.com.au/code-of-practice'
const COMPLAINTS_URL    = 'https://www.mclardymcshane.com.au/complaints-dispute-handling'
const IMPORTANT_INFO_URL = 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-Important-Information-Mar-26.pdf'
const WEBSITE_URL       = 'https://benchmarkinsurance.com.au/'
const LOGO_URL          = 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/BIB-Dark-(Horizontal).png'

const BROKER_PHONES = { 'Ben Rogers': '07 3666 8522', 'Jesse Roach': '07 2150 1260', 'Mitch Speer': '07 2150 1297' }
const BROKER_EMAILS = { 'Ben Rogers': 'ben@benchmarkinsurance.com.au', 'Jesse Roach': 'jesse@benchmarkinsurance.com.au', 'Mitch Speer': 'mitch@benchmarkinsurance.com.au' }
const BROKER_TITLES = { 'Ben Rogers': 'Principal', 'Jesse Roach': 'Co-Founder and Director', 'Mitch Speer': 'Co-Founder and Director' }

const QP_COVER_TYPES = [
  'Truck','Commercial Motor','Car','Plant & Equipment','Commercial Property',
  'Landlord','Public Liability','Professional Indemnity','Management Liability',
  'Carriers','Construction','Farm','Boat','Workers Compensation','PAC',
  'Home & Contents','Caravan','Motorbike','Residential Strata','Marine',
  'Cyber','Travel','Business Package','Other',
]

// ── Exact-match retail list (no substring matching) ──────────────────────────
const RETAIL_COVER_TYPES = [
  'Car','Commercial Motor','Motorbike','Caravan','Boat',
  'Landlord','Farm','Home & Contents','PAC',
]

const CLAIMS_MADE_TYPES = ['Professional Indemnity','Management Liability','Cyber','Directors & Officers']
const MONTHLY_LOADING_RATE = 0.11

const COVERAGE_OPTIONS = {
  'Commercial Property': ['Property Damage','Business Interruption','Public and Products Liability','Theft','Money','Glass','General Property','Equipment / Machinery Breakdown','Marine Transit','Management Liability'],
  Truck: ['Comprehensive Cover','Third Party Property Damage','Downtime Cover','Tools of Trade / General Property','Hire Costs','Excess Free Windscreen','Dangerous Goods Liability','Trailer Cover','Carriers Liability'],
  'Commercial Motor': ['Comprehensive Cover','Third Party Property Damage','Downtime Cover','Tools of Trade / General Property','Hire Costs','Excess Free Windscreen','Dangerous Goods Liability','Signwriting Cover','Trailer Cover'],
  Car: ['Comprehensive Cover','Third Party Property Damage','Hire Car After Accident','Excess Free Windscreen','Roadside Assistance','Choice of Repairer'],
  Boat: ['Accidental Damage','Theft','Storm Damage','Liability Cover','Contents / Personal Effects','Water Skiers Liability','Trailer Cover'],
  'Public Liability': ['Public Liability','Products Liability','Property in Physical or Legal Control','Care Custody and Control',"Tenant's Liability"],
  'Professional Indemnity': ['Professional Indemnity','Civil Liability','Defamation','Loss of Documents','Fraud and Dishonesty','Run-Off Cover'],
  'Management Liability': ['Directors and Officers Liability','Employment Practices Liability','Crime','Statutory Liability','Tax Audit','Corporate Entity Liability'],
  'Plant & Equipment': ['Accidental Damage','Theft','Hired In Plant','Road Risk','Breakdown Cover','Attachments Cover'],
  Landlord: ['Building','Contents','Tenant Default','Malicious Damage by Tenant','Theft by Tenant','Loss of Rent','Public Liability','Accidental Damage'],
  'Home & Contents': ['Home Building','Contents','Portable Cover','Accidental Damage','Motor Burnout','Flood Cover','Temporary Accommodation'],
  Farm: ['Homestead','Farm Property','Machinery','Livestock','Crop','Public Liability','Farm Transit','Accidental Damage'],
  Construction: ['Contract Works','Public Liability','Existing Structures','Materials in Transit','Tools of Trade','Plant and Equipment'],
  Carriers: ['Carriers Liability','Goods in Transit','Public Liability','Downtime Cover','Trailer Cover','Dangerous Goods Liability'],
  'Workers Compensation': ['Workers Compensation','Journey Cover','Voluntary Workers'],
  PAC: ['Weekly Benefits','Lump Sum Injury Benefits','Death Cover','Funeral Expenses'],
}

const INSURER_LIST = [
  'Allianz Australia Insurance Limited',
  'CGU Insurance Limited',
  'Vero Insurance Limited',
  'QBE Insurance (Australia) Limited',
  'Zurich Australian Insurance Limited',
  'Chubb Insurance Australia Limited',
  'Liberty Specialty Markets',
  'HDI Global Specialty SE – Australia',
  'Hollard Commercial Insurance',
  'Hollard Personal Insurance',
  'Ansvar Insurance Limited',
  'NTI Limited',
  'AIG Australia Limited',
  'Berkshire Hathaway Specialty Insurance (BHSI)',
  'Arch Insurance Australia',
  'Pacific International Insurance',
  'RelyOn Insurance',
  'Victor Insurance Australia',
  'Guild Insurance Limited',
  'Protecsure',
  'Blue Zebra Insurance',
  'Allstate Underwriting',
  'Clover Insurance',
  'Hutch Underwriting',
  'High Street Underwriting',
  'Coast Insurance',
  'Castle Insurance',
  'Market Lane Insurance Group',
  'UAA',
  'ProRisk',
  'GT Insurance',
  'PEN Underwriting',
  'ATL Insurance',
  'National Transport Insurance',
  'Other',
]

const QUOTE_DRAFT_STORAGE_PREFIX = 'benchmark:quote-pack-draft:'
const DOC_SLOT_FILE_NAMES = { quote: 'Quote', pds: 'PDS', record: 'Record of Answers' }

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtDate = (v) => { if (!v) return '—'; return new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) }
const parseCurr = (v) => (v || '').replace(/[^\d.]/g, '')
const formatCurr = (v) => { const n = Number(parseCurr(v)); if (!Number.isFinite(n) || n <= 0) return ''; return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
const formatCurrNumber = (n) => { if (!Number.isFinite(n) || n <= 0) return ''; return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
const getLoadedTotal = (v) => { const n = Number(parseCurr(v)); if (!Number.isFinite(n) || n <= 0) return ''; return formatCurrNumber(n * (1 + MONTHLY_LOADING_RATE)) }
const getMonthlyInstallment = (v) => { const n = Number(parseCurr(v)); if (!Number.isFinite(n) || n <= 0) return ''; return formatCurrNumber((n * (1 + MONTHLY_LOADING_RATE)) / 12) }
const getFortnightlyInstallment = (v) => { const n = Number(parseCurr(v)); if (!Number.isFinite(n) || n <= 0) return ''; return formatCurrNumber((n * (1 + MONTHLY_LOADING_RATE)) / 24) }
const addOneYear = (d) => { if (!d) return ''; const dt = new Date(d); if (isNaN(dt)) return ''; dt.setFullYear(dt.getFullYear() + 1); return dt.toISOString().split('T')[0] }
const todayIso = () => new Date().toISOString().split('T')[0]

// ── Exact-match retail detection ─────────────────────────────────────────────
const detectIsRetail = (coverType) =>
  RETAIL_COVER_TYPES.some(t => (coverType || '').trim().toLowerCase() === t.trim().toLowerCase())

const isClaimsMade = (coverType) =>
  CLAIMS_MADE_TYPES.some(t => (coverType || '').trim().toLowerCase() === t.trim().toLowerCase())

const buildInitialQuoteData = (client) => ({
  insured_name: client.company_name || '',
  contact_name: client.contact_name || '',
  greeting_text: '',
  cover_type: client.policy_type || '',
  risk_details: '',
  period_from: todayIso(),
  period_to: addOneYear(todayIso()),
  payment_type: 'annual',
  premium_funder: '',
  recommendation_reason: '',
  coverage_included: [],
  key_exclusions: '',
  doc_files: {},
  quotes: [
    { insurer: '', premium: '', excess: '', comments: '', recommended: true },
    { insurer: '', premium: '', excess: '', comments: '', recommended: false },
  ],
})

const getQuoteDraftStorageKey = (clientId) => `${QUOTE_DRAFT_STORAGE_PREFIX}${clientId || 'new'}`

const buildSlotDisplayName = (slotKey, originalName) => {
  const baseName = DOC_SLOT_FILE_NAMES[slotKey] || 'Document'
  const extMatch = `${originalName || ''}`.trim().match(/\.([a-zA-Z0-9]+)$/)
  const ext = extMatch?.[1]?.toLowerCase()
  return ext ? `${baseName}.${ext}` : baseName
}

const restoreQuoteDraft = (storedValue, fallbackData) => {
  if (!storedValue) return null
  try {
    const parsed = JSON.parse(storedValue)
    const draftQuoteData = parsed?.quoteData || {}
    const quotes = Array.isArray(draftQuoteData.quotes) && draftQuoteData.quotes.length > 0
      ? draftQuoteData.quotes : fallbackData.quotes
    return {
      step: Number.isFinite(parsed?.step) ? Math.min(Math.max(parsed.step, 1), 3) : 1,
      quoteData: {
        ...fallbackData, ...draftQuoteData,
        coverage_included: Array.isArray(draftQuoteData.coverage_included) ? draftQuoteData.coverage_included : fallbackData.coverage_included,
        doc_files: { ...fallbackData.doc_files, ...(draftQuoteData.doc_files || {}) },
        quotes,
      },
    }
  } catch { return null }
}

// ─── EMAIL HTML BUILDER ───────────────────────────────────────────────────────
export function buildQuotePackHtml({ quoteData, brokerName, brokerPhone, brokerTitle, isRetail: isRetailProp }) {
  const retail = isRetailProp !== undefined ? isRetailProp : detectIsRetail(quoteData.cover_type)
  const claimsMade = isClaimsMade(quoteData.cover_type)

  const rec = (quoteData.quotes || []).find(q => q.recommended) || quoteData.quotes?.[0]
  const alt = (quoteData.quotes || []).find(q => !q.recommended && (q.insurer || q.premium))

  const isMonthly = quoteData.payment_type === 'monthly'
  const isFortnightly = quoteData.payment_type === 'fortnightly'
  const isInstalments = isMonthly || isFortnightly

  const renderPremiumDisplay = (premium) => {
    if (!premium) return '—'
    if (!isInstalments) return premium
    if (isFortnightly) { const fn = getFortnightlyInstallment(premium); return fn ? `Approx. ${fn}/fortnight` : premium }
    const mn = getMonthlyInstallment(premium); return mn ? `Approx. ${mn}/month` : premium
  }

const paymentLabel = isFortnightly
    ? `Fortnightly via ${quoteData.premium_funder || 'Premium Funder'}`
    : isMonthly ? `Monthly via ${quoteData.premium_funder || 'Premium Funder'}`
    : 'Annual'

  const periodText = quoteData.period_from && quoteData.period_to
    ? `${fmtDate(quoteData.period_from)} to ${fmtDate(quoteData.period_to)}` : '—'

  const brokerDirectEmail = BROKER_EMAILS[brokerName] || BM.email
  const brokerSignoff = `<strong>${brokerName||'Your Broker'}</strong><br/>${brokerTitle?`${brokerTitle}<br/>`:''}${brokerPhone?`Direct: ${brokerPhone}<br/>`:''}<strong>Benchmark Insurance Brokers</strong><br/>${BM.phone} | <a href="mailto:${BM.email}" style="color:#1e3d30;">${BM.email}</a><br/><a href="${WEBSITE_URL}" style="color:#1e3d30;">benchmarkinsurance.com.au</a>`
  const footerLinks = `<a href="${FSG_URL}" style="color:#1e3d30;">Financial Services Guide</a> | <a href="${TERMS_URL}" style="color:#1e3d30;">Terms of Engagement</a> | <a href="${PRIVACY_URL}" style="color:#1e3d30;">Privacy Policy</a> | <a href="${CODE_URL}" style="color:#1e3d30;">Code of Practice</a> | <a href="${COMPLAINTS_URL}" style="color:#1e3d30;">Complaints &amp; Disputes</a>`

  const purchaseSubject = encodeURIComponent(`Yes, I'd like to proceed — ${quoteData.insured_name||'Client'} — ${quoteData.cover_type||'Insurance'}`)
  const purchaseBody = encodeURIComponent(`Hi ${brokerName?.split(' ')[0]||'Benchmark'},\n\nI've reviewed the quote and would like to proceed with the recommended option.\n\nInsured: ${quoteData.insured_name||'—'}\nCover Type: ${quoteData.cover_type||'—'}\nInsurer: ${rec?.insurer||'—'}\nPremium: ${rec?.premium||'—'}\n\nPlease go ahead and bind cover.\n\nKind regards,\n${quoteData.contact_name||quoteData.insured_name||'Client'}`)
  const purchaseHref = `mailto:${brokerDirectEmail}?subject=${purchaseSubject}&body=${purchaseBody}`

  const recRow = rec ? `
<tr style="background:rgba(30,61,48,0.04);">
<td style="padding:10px 12px;font-weight:700;color:#1e3d30;">${rec.insurer||'—'} <span style="background:#1e3d30;color:#fff;font-size:10px;padding:2px 7px;border-radius:10px;font-weight:600;white-space:nowrap;">Recommended</span></td>
<td style="padding:10px 12px;font-weight:700;">${renderPremiumDisplay(rec.premium)}</td>
<td style="padding:10px 12px;">${rec.excess||'—'}</td>
<td style="padding:10px 12px;">${rec.comments||'—'}</td>
</tr>` : ''

  const altRow = alt ? `
<tr>
<td style="padding:10px 12px;color:#6b8278;">${alt.insurer||'—'} <span style="font-size:10px;color:#6b8278;">(Alternative)</span></td>
<td style="padding:10px 12px;">${renderPremiumDisplay(alt.premium)}</td>
<td style="padding:10px 12px;">${alt.excess||'—'}</td>
<td style="padding:10px 12px;">${alt.comments||'—'}</td>
</tr>` : ''

  const includedItems = quoteData.coverage_included || []
  const includedBlock = includedItems.length > 0 ? `
<p style="margin:0 0 10px 0;font-weight:700;">What's Included</p>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;">
${includedItems.reduce((rows, item, i) => {
  if (i % 2 === 0) rows.push('<tr>')
  rows[rows.length-1] += `<td style="padding:5px 8px;width:50%;font-size:13px;color:#1a2e25;"><span style="color:#1e7a46;font-weight:800;margin-right:6px;">✓</span>${item}</td>`
  if (i % 2 === 1 || i === includedItems.length-1) rows[rows.length-1] += '</tr>'
  return rows
}, []).join('\n')}
</table>` : ''

const vehicleWarningsBlock = (() => {
    const ct = (quoteData.cover_type || '').toLowerCase()
    if (!ct.includes('truck') && !ct.includes('transport') && !ct.includes('commercial motor') && !ct.includes('plant')) return ''
    return `<p style="margin:0 0 10px 0;font-weight:700;">Important Notices — ${quoteData.cover_type_display || quoteData.cover_type}</p>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;"><tr><td style="background:#f8f9fa;border:1px solid #dee2e6;border-radius:8px;padding:16px 18px;"><table width="100%" cellpadding="0" cellspacing="0" border="0">${[
  ['Prior Insurance','Insurers usually provide a discount if you have advised us that previously you had continuous insurance. It is extremely important you have proof of this on file. In the event of a claim your insurer may ask for previous proof of insurance. If you cannot produce proof, your insurer may re-rate your policy to a much higher premium and put you in financial stress at claim time. Please call us if you need to discuss this.'],
  ['Additional Excesses','There are likely additional excesses for certain events in claims. Please ensure you check over your schedule and PDS to familiarise yourself with any additional excesses that may apply.'],
  ['Radius of Travel','Contact us immediately if you will be exceeding your radius of travel as listed in your Schedule of Insurance.'],
  ['Claims History','It is very important your insurer is aware of your full claims history as this forms part of the basis on which cover is offered. If you need to disclose any previous claims, please contact us immediately.'],
  ['Sum Insured','If your items are insured on a sum insured basis, your insurer will pay the lesser of market value or the sum insured stated on your schedule.'],
  ['Downtime Cover','Unless you have specifically opted for downtime cover (it will be noted on your schedule), there is no cover for loss of income in the event of a claim.'],
].map(([h,b])=>`<tr><td style="padding:8px 0;border-bottom:1px solid #e9ecef;vertical-align:top;"><p style="margin:0 0 3px 0;font-size:13px;font-weight:700;color:#1e3d30;">${h}</p><p style="margin:0;font-size:12.5px;line-height:1.7;color:#1a1a1a;">${b}</p></td></tr>`).join('')}</table></td></tr></table>`
  })()

  const exclusionsBlock = quoteData.key_exclusions ? `
<p style="margin:0 0 6px 0;font-weight:700;">Key Exclusions / Conditions to Note</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.8;color:#1a2e25;">${quoteData.key_exclusions.replace(/\n/g,'<br/>')}</p>` : ''

  const recommendationBlock = quoteData.recommendation_reason ? `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;">
<tr><td style="background:rgba(30,61,48,0.06);border-left:4px solid #1e3d30;padding:16px 20px;border-radius:0 8px 8px 0;">
<p style="margin:0 0 6px 0;font-size:11px;font-weight:700;letter-spacing:.6px;text-transform:uppercase;color:#1e3d30;">Our Recommendation</p>
<p style="margin:0;font-size:13px;line-height:1.8;color:#1a1a1a;">${quoteData.recommendation_reason.replace(/\n/g,'<br/>')}</p>
</td></tr></table>` : ''

  const docsHtml = [
    `<li>Financial Services Guide (FSG) — <a href="${FSG_URL}" style="color:#1e3d30;">View document</a></li>`,
    `<li>Benchmark Important Information — <a href="${IMPORTANT_INFO_URL}" style="color:#1e3d30;">View document</a></li>`,
    quoteData.doc_files?.quote?.name ? `<li>Quotation / Policy Schedule</li>` : null,
    quoteData.doc_files?.pds?.name ? `<li>Product Disclosure Statement (PDS)</li>` : null,
    quoteData.doc_files?.record?.name ? `<li>Record of Answers</li>` : null,
  ].filter(Boolean).join('')

  const changesSection = `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Changes to Your Information</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">You must tell the insurer anything that is relevant to their decision to insure you. If your circumstances have changed, please let us know so that we may advise you whether you need to tell your insurer and/or change your insurance cover.</p>`

  const claimsMadeSection = claimsMade ? `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">'Claims Made' Policy — Important Notice</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">This is a <strong>'claims made'</strong> policy. It requires that any claim made against you, or any incident that could potentially give rise to a claim, must be notified to the insurer <strong>during the period of insurance</strong>. If you do not notify during the current period, you may have no cover for the claim. You cannot claim after the policy has lapsed or been cancelled unless you have arranged run-off cover. Please contact us immediately if you become aware of any circumstance that may give rise to a claim.</p>` : ''

const dutySection = retail ? `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Target Market Determination</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">This product has been issued in accordance with the insurer's Target Market Determination. Based on the information you have provided, we are satisfied that this product is consistent with your likely objectives, financial situation and needs. A copy of the TMD is available on request or on the insurer's website.</p>
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Duty to Take Reasonable Care Not to Make a Misrepresentation</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">We remind you of your duty to take reasonable care not to make a misrepresentation. Full details are included in the Benchmark Important Information document attached. If you are in doubt about whether a particular matter should be disclosed, please contact your broker.</p>
${changesSection}
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Informed Consent — Commission Disclosure</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">We are required by law to obtain your informed consent to receive commission before arranging your policy. In arranging this insurance, your nominated insurer will pay Benchmark Insurance Brokers a commission, typically between <strong>5% and 26.5%</strong> of the premium (excluding taxes and levies), paid <strong>annually</strong>. This consent applies to future renewals of this policy. Before we bind cover, we will confirm your informed consent to us receiving this commission with you directly. Full details are included in our Terms of Engagement and FSG.</p>`: `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Target Market Determination</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">This product has been issued in accordance with the insurer's Target Market Determination. Based on the information you have provided, we are satisfied that this product is consistent with your likely objectives, financial situation and needs. A copy of the TMD is available on request or on the insurer's website.</p>
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Duty of Disclosure</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">We remind you of your duty of disclosure. Full details are included in the Benchmark Important Information document attached. If you are in doubt about whether a particular matter should be disclosed, please contact your broker.</p>
${claimsMadeSection}
${changesSection}`
  return `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
<head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;font-size:14px;color:#1a1a1a;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f4f4f4;">
<tr><td align="center" style="padding:20px 10px;">
<table role="presentation" width="950" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto;background:#ffffff;border:1px solid #cccccc;max-width:950px;width:950px;">
<tr><td align="center" style="background:#1e3d30;padding:26px;text-align:center;"><img src="${LOGO_URL}" width="280" alt="Benchmark Insurance Brokers" style="display:block;margin:0 auto;border:0;outline:none;"/></td></tr>
<tr><td style="padding:32px 42px;">
<p style="margin:0 0 16px 0;">${quoteData.greeting_text || `Dear <strong>${quoteData.contact_name||quoteData.insured_name||'Client'}</strong>,`}</p>
<p style="margin:0 0 16px 0;">Great news — we've done the hard work for you. After researching the market, we've put together your best insurance options below. We're confident we've found you strong cover at a competitive price, and we'd love to get you sorted.</p>
<p style="margin:0 0 10px 0;font-weight:700;">Quote Details</p>
<table role="presentation" width="100%" cellpadding="8" cellspacing="0" border="1" style="border-collapse:collapse;border-color:#cccccc;margin-bottom:18px;">
<tr><td style="background:#f5f5f5;width:38%;font-weight:700;">Insured Name</td><td>${quoteData.insured_name||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Cover Type</td><td>${quoteData.cover_type_display||quoteData.cover_type||'—'}</td></tr>
${quoteData.risk_details?`<tr><td style="background:#f5f5f5;font-weight:700;">Risk Details</td><td style="font-size:13px;line-height:1.7;">${quoteData.risk_details.replace(/\n/g,'<br/>')}</td></tr>`:''}
<tr><td style="background:#f5f5f5;font-weight:700;">Quote Period</td><td>${periodText}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Payment</td><td>${paymentLabel}</td></tr>
</table>
<p style="margin:0 0 10px 0;font-weight:700;">Your Quote Options</p>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="1" style="border-collapse:collapse;border-color:#cccccc;margin-bottom:20px;">
<tr style="background:#f5f5f5;">
<th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.4px;color:#6b8278;">Insurer</th>
<th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.4px;color:#6b8278;">Premium (incl. charges)</th>
<th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.4px;color:#6b8278;">Excess</th>
<th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.4px;color:#6b8278;">Comments</th>
</tr>
${recRow}${altRow}
</table>
${recommendationBlock}${includedBlock}${vehicleWarningsBlock}${exclusionsBlock}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:28px 0;">
<tr><td align="center"><a href="${purchaseHref}" style="display:inline-block;background:#1e3d30;color:#ffffff;font-family:Arial,sans-serif;font-size:16px;font-weight:700;padding:16px 40px;border-radius:10px;text-decoration:none;letter-spacing:.3px;">✅ Yes, I'd Like to Proceed</a></td></tr>
<tr><td align="center" style="padding-top:10px;font-size:12px;color:#6b8278;">Clicking this button opens a pre-filled email to your broker — just hit send to get your cover started.</td></tr>
</table>
<p style="margin:0 0 8px 0;font-weight:700;">Documents for Your Attention</p>
<ul style="margin:0 0 20px 0;padding-left:22px;font-size:13px;line-height:1.9;">${docsHtml}</ul>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;">
<tr><td style="background:#fffbe6;border:1px solid #f5d060;border-radius:8px;padding:14px 18px;">
<p style="margin:0 0 6px 0;font-size:13px;font-weight:700;color:#92600a;">Record of Answers — Please Review</p>
<p style="margin:0;font-size:13px;line-height:1.7;color:#1a1a1a;">A Record of Answers is enclosed with this quote, summarising the information you have provided to us. <strong>Please review this document carefully.</strong> If any details are incorrect or have changed, you must notify us immediately before proceeding. By instructing us to bind cover, you confirm that the information contained in the Record of Answers is true, correct and complete to the best of your knowledge.</p>
</td></tr></table>
<p style="margin:0 0 16px 0;font-size:13px;line-height:1.7;">Please also review all other enclosed documents carefully — in particular the <strong>Product Disclosure Statement</strong> and <strong>Benchmark Important Information</strong>, which outline important terms, conditions and your rights.</p>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:20px 0;">
<tr><td style="background:#1e3d30;padding:20px 26px;">
<p style="margin:0;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:rgba(255,255,255,0.6);">Benchmark Insurance Brokers</p>
<p style="margin:6px 0 8px 0;font-size:20px;font-weight:800;color:#ffffff;">Because Standards Matter.</p>
<p style="margin:0;font-size:12px;color:rgba(255,255,255,0.75);">Personalised service. Practical advice. Strong claims advocacy.</p>
</td></tr></table>
${dutySection}
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Our Remuneration</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">In return for the services we provide, we may receive a commission of usually between 5% and 26.5% of the premium paid (excluding relevant taxes, charges and levies) which is paid to us by the insurer, and/or a fee payable by you. Full details are included in our Terms of Engagement.</p>
${isInstalments ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;"><tr><td style="background:#fffbe6;border:1px solid #f5d060;border-radius:8px;padding:14px 18px;"><p style="margin:0 0 6px 0;font-size:13px;font-weight:700;color:#92600a;">Premium Funding — Important Notice</p><p style="margin:0;font-size:13px;line-height:1.7;color:#1a1a1a;">You have chosen to pay your premium by ${isFortnightly ? 'fortnightly' : 'monthly'} instalments via ${quoteData.premium_funder || 'your premium funder'}. Please be aware that if your policy is cancelled before the expiry date, the premium funder may charge the full interest applicable to the funding arrangement and there may be no refund of our commission or fees. Some insurers apply minimum premiums which may further reduce any refund. Please contact us before making any changes to a funded policy.</p></td></tr></table>` : ''}
<p style="margin:0 0 12px 0;font-size:13px;line-height:1.7;">This quote is valid for <strong>30 days</strong> from the date of issue. Cover is not bound until you have confirmed your instructions and we have confirmed binding in writing.</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;">Any questions? Call us on <a href="tel:${BM.phone}" style="color:#1e3d30;">${BM.phone}</a> or simply reply to this email.</p>
<p style="margin:0;">Yours faithfully,<br/><br/>${brokerSignoff}</p>
</td></tr>
<tr><td style="background:#f0f4f2;border-top:1px solid #d7e5de;padding:18px 42px;">
<p style="margin:0 0 6px 0;font-size:11px;color:#5f746c;line-height:1.7;"><strong>${BM.name}</strong> trading as <strong>${BM.trading}</strong> | ABN ${BM.abn} | AR No. ${BM.ar}<br/>Authorised Representative of <strong>${BM.licensee}</strong> | ABN ${BM.lic_abn} | AFSL ${BM.lic_afsl}</p>
<p style="margin:0;font-size:11px;line-height:1.8;">${footerLinks}</p>
</td></tr>
</table>
</td></tr></table>
</body>
</html>`
}

// ─── DOC UPLOAD SLOT ──────────────────────────────────────────────────────────
function DocUploadSlot({ slotKey, label, fileName, uploading, onFileDrop, onRemove, required }) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef(null)
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFileDrop(slotKey, f) }
  const handleInput = (e) => { const f = e.target.files?.[0]; if (f) onFileDrop(slotKey, f); e.target.value = '' }
  const hasFile = !!fileName
  const showRequired = required && !hasFile
  return (
    <div style={{ border: `1.5px solid ${hasFile ? 'var(--forest)' : showRequired ? 'var(--danger)' : 'var(--border)'}`, borderRadius: 10, background: hasFile ? 'rgba(30,61,48,0.06)' : 'var(--surface2)', overflow: 'hidden', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: hasFile ? 'var(--forest)' : showRequired ? 'var(--danger)' : 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {hasFile ? 'Attached' : 'Attach'} — {label}
          {required && <span style={{ fontSize: 10, fontWeight: 700, color: hasFile ? 'var(--forest)' : 'var(--danger)', background: hasFile ? 'rgba(30,61,48,0.1)' : 'rgba(220,38,38,0.1)', padding: '1px 6px', borderRadius: 6 }}>{hasFile ? '✓ Required' : 'Required'}</span>}
        </div>
        {hasFile && <button onClick={() => onRemove(slotKey)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14 }}>✕</button>}
      </div>
      <div onDragOver={(e) => { e.preventDefault(); setDragOver(true) }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => inputRef.current?.click()}
        style={{ margin: '0 10px 10px', padding: '8px 12px', borderRadius: 7, border: `1.5px dashed ${dragOver ? 'var(--forest)' : hasFile ? 'var(--forest)' : showRequired ? 'rgba(220,38,38,0.4)' : 'rgba(107,130,120,0.4)'}`, background: dragOver ? 'rgba(30,61,48,0.08)' : 'transparent', cursor: 'pointer', minHeight: 36, display: 'flex', alignItems: 'center' }}>
        {uploading ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>Uploading...</span>
          : hasFile ? <span style={{ fontSize: 11, color: 'var(--forest)', fontWeight: 600 }}>{fileName}</span>
          : <span style={{ fontSize: 11, color: showRequired ? 'var(--danger)' : 'var(--muted)' }}>{showRequired ? 'Required — drop file or click to upload' : 'Drop file or click to upload'}</span>}
        <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={handleInput} />
      </div>
    </div>
  )
}

// ─── SEND CONFIRMATION MODAL ──────────────────────────────────────────────────
function SendConfirmModal({ quoteData, client, brokerEmail, sendSms, isRetail, claimsMade, onConfirm, onCancel, sending }) {
  const checks = [
    { id: 'insured', label: `Insured name is correct — ${quoteData.insured_name || '—'}` },
    { id: 'cover', label: `Cover type is correct — ${quoteData.cover_type || '—'}` },
    { id: 'insurer', label: `Recommended insurer is correct — ${(quoteData.quotes || []).find(q => q.recommended)?.insurer || '—'}` },
    { id: 'premium', label: `Premium is correct — ${(quoteData.quotes || []).find(q => q.recommended)?.premium || '—'}` },
    { id: 'period', label: `Policy period is correct — ${quoteData.period_from ? `${quoteData.period_from} to ${quoteData.period_to}` : '—'}` },
    { id: 'compliance', label: `Compliance classification is correct — ${isRetail ? 'Retail (TMD + Misrep + Informed Consent)' : claimsMade ? 'Claims-Made (DoD + Claims Notice)' : 'Wholesale (Duty of Disclosure)'}` },
    { id: 'record', label: 'Record of Answers is attached and reviewed' },
    { id: 'email', label: `Sending to correct email — ${client.email}` },
  ]
  const [checked, setChecked] = useState({})
  const allChecked = checks.every(c => checked[c.id])
  const toggle = (id) => setChecked(prev => ({ ...prev, [id]: !prev[id] }))

  return (
    <div className="overlay" style={{ zIndex: 400 }}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <div className="modal-title">✅ Confirm before sending</div>
          <button className="modal-close" onClick={onCancel}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.6 }}>
            Check each item below before sending. This quote pack will be emailed to <strong>{client.email}</strong> and CC'd to <strong>{brokerEmail}</strong>.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {checks.map(c => (
              <div key={c.id} onClick={() => toggle(c.id)} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${checked[c.id] ? 'var(--forest)' : 'var(--border)'}`, background: checked[c.id] ? 'rgba(30,61,48,0.05)' : 'var(--surface2)', cursor: 'pointer', userSelect: 'none', transition: 'all .15s' }}>
                <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${checked[c.id] ? 'var(--forest)' : 'var(--border)'}`, background: checked[c.id] ? 'var(--forest)' : '#fff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0, marginTop: 1 }}>{checked[c.id] ? '✓' : ''}</div>
                <span style={{ fontSize: 13, fontWeight: checked[c.id] ? 600 : 400, color: checked[c.id] ? 'var(--text)' : 'var(--muted)', lineHeight: 1.4 }}>{c.label}</span>
              </div>
            ))}
          </div>
          {sendSms && (
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(242,101,34,0.06)', border: '1.5px solid rgba(242,101,34,0.2)', fontSize: 12, color: 'var(--orange)', fontWeight: 600, marginBottom: 16 }}>
              💬 SMS notification will also be sent to {client.phone || 'client'}
            </div>
          )}
          {!allChecked && (
            <div style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 600, marginBottom: 12 }}>
              ⚠ Please confirm all items above before sending
            </div>
          )}
          <div className="form-actions" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" style={{ background: allChecked ? '#1e7a46' : '#9ca3af' }} disabled={!allChecked || sending} onClick={onConfirm}>
              {sending ? 'Sending...' : '✉ Send Quote Pack'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── MAIN MODAL ───────────────────────────────────────────────────────────────
export function QuotePackModal({ client, user, onClose, showToast, loadAll }) {
  const brokerName  = user?.name || 'Your Broker'
  const brokerPhone = BROKER_PHONES[brokerName] || BM.phone
  const brokerEmail = BROKER_EMAILS[brokerName] || BM.email
  const brokerTitle = BROKER_TITLES[brokerName] || 'Broker'
  const defaultQuoteData = useMemo(() => buildInitialQuoteData(client), [client.company_name, client.policy_type])
  const draftStorageKey = useMemo(() => getQuoteDraftStorageKey(client.id), [client.id])
  const draftHydratedRef = useRef(false)

  const [step, setStep]                 = useState(1)
  const [saving, setSaving]             = useState(false)
  const [sending, setSending]           = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState({})
  const [polishingAi, setPolishingAi]   = useState(false)
  const [polishingRisk, setPolishingRisk] = useState(false)
  const [sendSms, setSendSms]           = useState(false)
  const [showConfirm, setShowConfirm]   = useState(false)
  // null = auto-detect, true = force retail, false = force wholesale
  const [retailOverride, setRetailOverride] = useState(null)
  const [quoteData, setQuoteData]       = useState(defaultQuoteData)

  // Compute retail classification
  const isRetail = retailOverride !== null ? retailOverride : detectIsRetail(quoteData.cover_type)
  const claimsMadeCover = isClaimsMade(quoteData.cover_type)
  const hasRecordOfAnswers = !!quoteData.doc_files?.record?.name

  const workingCompliance = useMemo(
    () => buildComplianceState(client, { policy_type: quoteData.cover_type || client.policy_type || '' }),
    [client, quoteData.cover_type]
  )

  useEffect(() => {
    draftHydratedRef.current = false
    setStep(1)
    setQuoteData(defaultQuoteData)
    setRetailOverride(null)
    if (typeof window === 'undefined') { draftHydratedRef.current = true; return }
    const restoredDraft = restoreQuoteDraft(window.sessionStorage.getItem(draftStorageKey), defaultQuoteData)
    if (restoredDraft) { setStep(restoredDraft.step); setQuoteData(restoredDraft.quoteData) }
    draftHydratedRef.current = true
  }, [defaultQuoteData, draftStorageKey])

  useEffect(() => {
    if (!draftHydratedRef.current || typeof window === 'undefined') return
    window.sessionStorage.setItem(draftStorageKey, JSON.stringify({ step, quoteData }))
  }, [draftStorageKey, quoteData, step])

  const update = (key, val) => setQuoteData(prev => ({ ...prev, [key]: val }))
  const updateQuote = (index, key, val) =>
    setQuoteData(prev => ({ ...prev, quotes: prev.quotes.map((q, i) => i === index ? { ...q, [key]: val } : q) }))
  const toggleCoverage = (item) =>
    setQuoteData(prev => ({ ...prev, coverage_included: prev.coverage_included.includes(item) ? prev.coverage_included.filter(c => c !== item) : [...prev.coverage_included, item] }))
  const handlePeriodFrom = (val) => { update('period_from', val); if (val) update('period_to', addOneYear(val)) }
  const clearSavedDraft = () => { if (typeof window !== 'undefined') window.sessionStorage.removeItem(draftStorageKey) }

  const handleFileDrop = async (slotKey, file) => {
    setUploadingDoc(prev => ({ ...prev, [slotKey]: true }))
    try {
      const ext = `${file.name || ''}`.includes('.') ? file.name.split('.').pop()?.toLowerCase() : ''
      const displayName = buildSlotDisplayName(slotKey, file.name)
      const path = `quote-packs/${client.id}/${slotKey}-${Date.now()}${ext ? `.${ext}` : ''}`
      const { error } = await supabase.storage.from('documents').upload(path, file, { upsert: true })
      if (error) throw error
      const { data } = supabase.storage.from('documents').getPublicUrl(path)
      setQuoteData(prev => ({ ...prev, doc_files: { ...prev.doc_files, [slotKey]: { name: displayName, original_name: file.name, path, url: data.publicUrl } } }))
      showToast(`Attached ${displayName}`)
    } catch (e) { showToast(`Upload failed: ${e.message}`) }
    setUploadingDoc(prev => ({ ...prev, [slotKey]: false }))
  }

  const handleFileRemove = async (slotKey) => {
    const existing = quoteData.doc_files?.[slotKey]
    if (existing?.path) await supabase.storage.from('documents').remove([existing.path])
    setQuoteData(prev => { const files = { ...prev.doc_files }; delete files[slotKey]; return { ...prev, doc_files: files } })
  }

  const polishWithAi = async () => {
    setPolishingAi(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/draft-quote-pack`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_name: client.contact_name || client.company_name, insured_name: quoteData.insured_name, policy_type: quoteData.cover_type, risk_specifics: quoteData.risk_details || '', start_date: quoteData.period_from, discussion_type: 'phone', recommendation_reason: quoteData.recommendation_reason, ai_notes: '', quotes: quoteData.quotes, broker_name: brokerName, tone: 'warm, plain language, practical australian insurance broker, direct and professional' }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.error || 'AI failed')
      if (result.draft?.recommendation_text) { update('recommendation_reason', result.draft.recommendation_text); showToast('✨ Recommendation generated') }
    } catch (e) { showToast(`AI error: ${e.message}`) }
    setPolishingAi(false)
  }

  const polishRiskDetails = async () => {
    if (!quoteData.risk_details.trim()) return showToast('Add some risk details first')
    setPolishingRisk(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/polish-bind-notes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ coverData: { bind_client_summary: quoteData.risk_details, bind_tmd_reason: '', bind_insurer_reason: '', bind_payment_notes: '', cover_type: quoteData.cover_type, insured_name: quoteData.insured_name, insurer: quoteData.quotes?.[0]?.insurer || '' }, brokerName }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.error || 'Polish failed')
      if (result.polished?.bind_client_summary) { update('risk_details', result.polished.bind_client_summary); showToast('✨ Risk details polished') }
    } catch (e) { showToast(`Polish failed: ${e.message}`) }
    setPolishingRisk(false)
  }

  const htmlPreview = buildQuotePackHtml({ quoteData, brokerName, brokerPhone, brokerTitle, isRetail })

  const openPdf = () => {
    const w = window.open('', '_blank', 'width=1200,height=900')
    if (!w) return
    w.document.write(`<html><head><title>Quote Pack</title><style>*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}body{margin:0;background:#f4f4f4}.bar{position:fixed;top:16px;right:16px;z-index:9999;display:flex;gap:8px}.btn{background:#1e3d30;color:#fff;border:none;padding:10px 20px;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer}.cls{background:#6b8278}@media print{.bar{display:none!important}@page{margin:0;size:A4}}</style></head><body><div class="bar"><button class="btn" onclick="window.print()">Print / Save PDF</button><button class="btn cls" onclick="window.close()">Close</button></div>${htmlPreview}</body></html>`)
    w.document.close(); w.focus()
  }

  const saveRecord = async () => {
    setSaving(true)
    try {
      const { error } = await supabase.from('quote_packs').insert({
        client_id: client.id, created_by: brokerName,
        title: `${quoteData.cover_type_display || quoteData.cover_type || 'Quote'} — ${quoteData.insured_name}`,
        status: 'Draft',
        quote_data: { ...quoteData, compliance_snapshot: workingCompliance, is_retail: isRetail },
        html_preview: htmlPreview,
      })
      if (error) throw error
      if (loadAll) await loadAll()
      clearSavedDraft()
      showToast('Quote pack saved')
      onClose()
    } catch (e) { showToast(`Error saving: ${e.message}`) }
    setSaving(false)
  }

  const doSendEmail = async () => {
    if (!client.email) return showToast('No email address on file for this client')
    if (!hasRecordOfAnswers) return showToast('Record of Answers is required — please attach before sending')
    setSending(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const subject = `Quote Options — ${quoteData.insured_name || client.company_name} — ${quoteData.cover_type_display || quoteData.cover_type || 'Insurance'}`
      const attachmentFiles = Object.values(quoteData.doc_files || {}).map(f => ({ name: f.name, path: f.path }))
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-quote-pack`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteData: { ...quoteData, compliance_snapshot: workingCompliance, is_retail: isRetail }, brokerName, brokerEmail, clientEmail: client.email, clientPhone: sendSms ? (client.phone || null) : null, clientName: client.company_name || null, clientId: client.id || null, subject, attachmentFiles, ccBroker: brokerEmail }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.error || 'Send failed')
      await supabase.from('quote_packs').insert({
        client_id: client.id, created_by: brokerName,
        title: `${quoteData.cover_type_display || quoteData.cover_type || 'Quote'} — ${quoteData.insured_name}`,
        status: 'Sent',
        quote_data: { ...quoteData, compliance_snapshot: workingCompliance, is_retail: isRetail },
        html_preview: htmlPreview,
      })
      await logClientEmailAction({
        supabase, client, userName: brokerName,
        emailType: 'Quote Pack', subject, recipient: client.email,
        snapshotUpdates: { last_quote_pack_sent_at: new Date().toISOString() },
        activityText: `Quote pack sent by ${brokerName} (CC: ${brokerEmail})`,
      })
      if (loadAll) await loadAll()
      clearSavedDraft()
      showToast(`Quote sent to ${client.email} (CC: ${brokerEmail})`)
      setShowConfirm(false)
      onClose()
    } catch (e) { showToast(`Send failed — ${e.message}`) }
    setSending(false)
  }

  const coverageOptions = COVERAGE_OPTIONS[quoteData.cover_type] || []
  const steps = ['Quote Details', 'Cover & Docs', 'Preview']
  const isInstalments = quoteData.payment_type === 'monthly' || quoteData.payment_type === 'fortnightly'

  // Compliance badge helpers
  const complianceBadgeText = isRetail
    ? '✓ Retail — Misrep + TMD + Informed Consent'
    : claimsMadeCover ? '✓ Claims-Made — DoD + Claims Notice'
    : '✓ Wholesale — Duty of Disclosure'
  const complianceBadgeColor = isRetail ? 'var(--forest)' : '#2563eb'
  const complianceBadgeBg = isRetail ? 'rgba(30,61,48,0.1)' : 'rgba(59,130,246,0.1)'

  return (
    <>
      {showConfirm && (
        <SendConfirmModal
          quoteData={quoteData}
          client={client}
          brokerEmail={brokerEmail}
          sendSms={sendSms}
          isRetail={isRetail}
          claimsMade={claimsMadeCover}
          onConfirm={doSendEmail}
          onCancel={() => setShowConfirm(false)}
          sending={sending}
        />
      )}

      <div className="overlay">
        <div className="modal" style={{ maxWidth: 1100, width: '96vw', maxHeight: '94vh' }}>

          {/* Header */}
          <div className="modal-head">
            <div>
              <div className="modal-title">Prepare Quote Pack</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{client.company_name}</span>
                {quoteData.cover_type && <span>· {quoteData.cover_type}</span>}

                {/* Compliance badge */}
                <span style={{ padding: '1px 8px', borderRadius: 10, background: complianceBadgeBg, color: complianceBadgeColor, fontWeight: 600, fontSize: 11 }}>
                  {complianceBadgeText}
                </span>
                {claimsMadeCover && retailOverride !== true && (
                  <span style={{ padding: '1px 8px', borderRadius: 10, background: 'rgba(139,92,246,0.1)', color: '#7c3aed', fontWeight: 600, fontSize: 11 }}>+ Claims-Made notice</span>
                )}

                {/* Override pills */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 4 }}>
                  <span style={{ fontSize: 10, color: 'var(--muted)', marginRight: 2 }}>Override:</span>
                  {[
                    { label: 'Auto', val: null },
                    { label: 'Wholesale', val: false },
                    { label: 'Retail', val: true },
                  ].map(opt => (
                    <button key={String(opt.val)} onClick={() => setRetailOverride(opt.val)} style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 10, border: '1.5px solid',
                      borderColor: retailOverride === opt.val ? complianceBadgeColor : 'var(--border)',
                      background: retailOverride === opt.val ? complianceBadgeBg : 'transparent',
                      color: retailOverride === opt.val ? complianceBadgeColor : 'var(--muted)',
                      cursor: 'pointer', fontFamily: 'var(--font)', fontWeight: retailOverride === opt.val ? 700 : 400,
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>
            </div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          {/* Step indicator */}
          <div style={{ padding: '12px 24px', borderBottom: '1.5px solid var(--border)', background: 'var(--surface2)', display: 'flex', gap: 0 }}>
            {steps.map((label, index) => {
              const number = index + 1
              const active = step === number
              return (
                <div key={label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <div onClick={() => setStep(number)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: active ? 'var(--forest)' : 'transparent', cursor: 'pointer' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: active ? '#fff' : 'var(--border)', color: active ? 'var(--forest)' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{number}</div>
                    <span style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: active ? '#fff' : 'var(--muted)' }}>{label}</span>
                  </div>
                  {index < steps.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--border)', margin: '0 4px' }} />}
                </div>
              )
            })}
          </div>

          <div className="modal-body" style={{ overflowY: 'auto', maxHeight: 'calc(94vh - 180px)' }}>

            {/* ── STEP 1: Quote Details ── */}
            {step === 1 && (
              <div>
<div className="form-grid">
                  <div>
                    <label className="form-label">Insured Name <span style={{fontWeight:400,color:'var(--muted)'}}>— legal entity on the policy</span></label>
                    <input className="form-input" value={quoteData.insured_name} onChange={e=>update('insured_name',e.target.value)} />
                  </div>
<div>
                    <label className="form-label">Attention / Contact <span style={{fontWeight:400,color:'var(--muted)'}}>— who the email greets</span></label>
                    <input className="form-input" value={quoteData.contact_name||''} onChange={e=>update('contact_name',e.target.value)} placeholder="e.g. Jen Grimsey" />
                  </div>
                  <div>
                    <label className="form-label">Email Greeting <span style={{fontWeight:400,color:'var(--muted)'}}>— exactly as it appears</span></label>
                    <input className="form-input" value={quoteData.greeting_text||''} onChange={e=>update('greeting_text',e.target.value)} placeholder="e.g. Hi Samantha," />
                  </div>
                  <div>
                    <label className="form-label">Cover Type</label>
                    <select className="form-select" value={quoteData.cover_type} onChange={e => { update('cover_type', e.target.value); update('coverage_included', []); setRetailOverride(null) }}>
                      <option value="">Select...</option>
                      {QP_COVER_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Cover Type Display Name <span style={{fontWeight:400,color:'var(--muted)'}}>— overrides what appears in the email</span></label>
                    <input className="form-input" value={quoteData.cover_type_display||''} onChange={e=>update('cover_type_display',e.target.value)} placeholder={quoteData.cover_type||'e.g. Transport Pack, Farm Combined'} />
                  </div>
                  <div>
                    <label className="form-label">Payment Type</label>
                    <select className="form-select" value={quoteData.payment_type} onChange={e => update('payment_type', e.target.value)}>
                      <option value="annual">Annual</option>
<option value="monthly">Monthly (Premium Funding)</option>
                      <option value="fortnightly">Fortnightly (Premium Funding)</option>
                    </select>
                  </div>
                  {isInstalments && (
                    <div><label className="form-label">Premium Funder</label><select className="form-select" value={quoteData.premium_funder} onChange={e => update('premium_funder', e.target.value)}><option value="">Select...</option><option>Attvest</option><option>Victory</option></select></div>
                  )}
                  <div><label className="form-label">Quote Period From</label><input className="form-input" type="date" value={quoteData.period_from} onChange={e => handlePeriodFrom(e.target.value)} /></div>
                  <div><label className="form-label">Quote Period To</label><input className="form-input" type="date" value={quoteData.period_to} onChange={e => update('period_to', e.target.value)} /></div>
                  <div className="full">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <label className="form-label" style={{ margin: 0 }}>Risk Details</label>
                      <button className="btn btn-secondary btn-sm" onClick={polishRiskDetails} disabled={polishingRisk} style={{ fontSize: 11 }}>{polishingRisk ? '✨ Polishing...' : '✨ Polish with AI'}</button>
                    </div>
                    <textarea className="form-input" rows={4} value={quoteData.risk_details} onChange={e => update('risk_details', e.target.value)} placeholder="Describe the risk — vehicle types, sums insured, business activities, any relevant history..." />
                  </div>
                </div>
				
                <div style={{ marginTop: 20, fontSize: 13, fontWeight: 600, color: 'var(--forest)', marginBottom: 12 }}>Quote Options</div>
                {quoteData.quotes.map((quote, index) => (
                  <div key={index} style={{ marginBottom: 16, padding: '14px 16px', border: `1.5px solid ${index === 0 ? 'var(--forest)' : 'var(--border)'}`, borderRadius: 12, background: index === 0 ? 'rgba(30,61,48,0.03)' : 'var(--surface2)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: index === 0 ? 'var(--forest)' : 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>{index === 0 ? 'Recommended Option' : 'Alternative Option'}</div>
                    <div className="form-grid">
                      <div>
                        <label className="form-label">Insurer</label>
                        <select className="form-select"
                          value={INSURER_LIST.includes(quote.insurer) ? quote.insurer : (quote.insurer ? 'Other' : '')}
                          onChange={e => { if (e.target.value === 'Other') updateQuote(index, 'insurer', ''); else updateQuote(index, 'insurer', e.target.value) }}>
                          <option value="">Select insurer...</option>
                          {INSURER_LIST.map(ins => <option key={ins} value={ins}>{ins}</option>)}
                        </select>
                        {(!INSURER_LIST.includes(quote.insurer) || quote.insurer === '') && (
                          <input className="form-input" style={{ marginTop: 6 }} value={quote.insurer} onChange={e => updateQuote(index, 'insurer', e.target.value)} placeholder="Type insurer name if not listed..." />
                        )}
                      </div>
                      <div>
                        <label className="form-label">Total Premium</label>
                        <input className="form-input" value={quote.premium} onChange={e => updateQuote(index, 'premium', parseCurr(e.target.value))} onBlur={e => updateQuote(index, 'premium', formatCurr(e.target.value))} onFocus={e => updateQuote(index, 'premium', parseCurr(e.target.value))} placeholder="e.g. 1250.00" />
                        {isInstalments && (
                          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
                            11% loading: {getLoadedTotal(quote.premium) || '—'} total
                            {quoteData.payment_type === 'monthly' && getMonthlyInstallment(quote.premium) ? ` (${getMonthlyInstallment(quote.premium)}/month)` : ''}
                            {quoteData.payment_type === 'fortnightly' && getFortnightlyInstallment(quote.premium) ? ` (${getFortnightlyInstallment(quote.premium)}/fortnight)` : ''}
                          </div>
                        )}
                      </div>
                      <div><label className="form-label">Excess</label><input className="form-input" value={quote.excess} onChange={e => updateQuote(index, 'excess', parseCurr(e.target.value))} onBlur={e => updateQuote(index, 'excess', formatCurr(e.target.value))} onFocus={e => updateQuote(index, 'excess', parseCurr(e.target.value))} placeholder="e.g. 500.00" /></div>
                      <div><label className="form-label">Comments</label><input className="form-input" value={quote.comments} onChange={e => updateQuote(index, 'comments', e.target.value)} placeholder="e.g. Includes agreed value" /></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── STEP 2: Cover & Docs ── */}
            {step === 2 && (
              <div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--forest)', marginBottom: 8 }}>What's Included</div>
                  {coverageOptions.length === 0
                    ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>Select a cover type on step 1 to load common inclusions.</div>
                    : <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {coverageOptions.map(item => {
                        const checked = quoteData.coverage_included.includes(item)
                        return (
                          <div key={item} onClick={() => toggleCoverage(item)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1.5px solid ${checked ? 'var(--forest)' : 'var(--border)'}`, borderRadius: 8, background: checked ? 'var(--forest-subtle)' : 'var(--surface2)', cursor: 'pointer' }}>
                            <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? 'var(--forest)' : 'var(--border)'}`, background: checked ? 'var(--forest)' : '#fff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>{checked ? '✓' : ''}</div>
                            <div style={{ fontSize: 12, fontWeight: checked ? 600 : 400 }}>{item}</div>
                          </div>
                        )
                      })}
                    </div>
                  }
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label className="form-label">Key Exclusions / Conditions to Note</label>
                  <textarea className="form-input" rows={4} value={quoteData.key_exclusions} onChange={e => update('key_exclusions', e.target.value)} placeholder="Be specific — this appears verbatim in the client email" />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <label className="form-label" style={{ margin: 0 }}>Why are you recommending this option?</label>
                    <button className="btn btn-secondary btn-sm" onClick={polishWithAi} disabled={polishingAi}>{polishingAi ? '✨ Generating...' : '✨ AI Polish'}</button>
                  </div>
                  <textarea className="form-input" rows={6} value={quoteData.recommendation_reason} onChange={e => update('recommendation_reason', e.target.value)} placeholder="Explain why this insurer and product is the right fit for this client..." />
                </div>

                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--forest)', marginBottom: 4 }}>Documents to Attach</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>FSG and Benchmark Important Information are always included as links. Upload any additional files below.</div>
                  <DocUploadSlot slotKey="quote" label="Quotation / Policy Schedule" fileName={quoteData.doc_files?.quote?.name || null} uploading={!!uploadingDoc.quote} onFileDrop={handleFileDrop} onRemove={handleFileRemove} />
                  <DocUploadSlot slotKey="pds" label="Product Disclosure Statement" fileName={quoteData.doc_files?.pds?.name || null} uploading={!!uploadingDoc.pds} onFileDrop={handleFileDrop} onRemove={handleFileRemove} />
                  <DocUploadSlot slotKey="record" label="Record of Answers" fileName={quoteData.doc_files?.record?.name || null} uploading={!!uploadingDoc.record} onFileDrop={handleFileDrop} onRemove={handleFileRemove} required />
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <div style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--forest-border)', background: 'var(--forest-subtle)', fontSize: 11, color: 'var(--forest)', fontWeight: 600 }}>✓ FSG — always included as link</div>
                    <div style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--forest-border)', background: 'var(--forest-subtle)', fontSize: 11, color: 'var(--forest)', fontWeight: 600 }}>✓ Benchmark Important Information — always included as link</div>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: Preview ── */}
            {step === 3 && (
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button className="btn btn-primary" onClick={saveRecord} disabled={saving}>{saving ? 'Saving...' : 'Save Record'}</button>
                  <button
                    className="btn btn-primary"
                    style={{ background: hasRecordOfAnswers && client.email ? '#1e7a46' : '#9ca3af' }}
                    onClick={() => {
                      if (!client.email) return showToast('No email address on file for this client')
                      if (!hasRecordOfAnswers) return showToast('Record of Answers is required — please attach before sending')
                      setShowConfirm(true)
                    }}
                    disabled={!client.email || !hasRecordOfAnswers}
                  >
                    ✉ Send Quote
                  </button>
                  <button className="btn btn-secondary" onClick={openPdf}>Open PDF</button>
                  {!client.email && <span style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 600 }}>No email on file</span>}
                  {!hasRecordOfAnswers && <span style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 600 }}>⚠ Record of Answers required before sending</span>}
                </div>

                {brokerEmail && <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>Broker will be CC'd on the email ({brokerEmail})</div>}

                <div onClick={() => setSendSms(v => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${sendSms ? 'var(--forest)' : 'var(--border)'}`, background: sendSms ? 'var(--forest)' : '#fff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>{sendSms ? '✓' : ''}</div>
                  <span style={{ fontSize: 12, color: sendSms ? 'var(--forest)' : 'var(--muted)', fontWeight: sendSms ? 600 : 400 }}>Also send SMS notification to client{client.phone ? ` (${client.phone})` : ' — no phone on file'}</span>
                </div>

                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Email Preview</div>
                <div style={{ background: '#fbfcfb', border: '1.5px solid var(--border)', borderRadius: 12, overflow: 'auto', maxHeight: 600 }} dangerouslySetInnerHTML={{ __html: htmlPreview }} />
              </div>
            )}
          </div>

          <div style={{ padding: '14px 24px', borderTop: '1.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', background: 'var(--surface)' }}>
            <button className="btn btn-secondary" onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}>{step === 1 ? 'Cancel' : '← Back'}</button>
            {step < 3 && <button className="btn btn-primary" onClick={() => setStep(s => s + 1)}>Next →</button>}
          </div>
        </div>
      </div>
    </>
  )
}
